<template>
  <FormSheet
    :open="open"
    :title="$t('Подробнее об устройстве')"
    :width="560"
    @update:open="onOpenChange"
  >
    <div v-if="device" class="flex flex-col gap-4">
      <!-- Meta -->
      <div class="grid grid-cols-2 gap-3 text-sm">
        <div>
          <div class="text-muted-foreground">{{ $t("ИИН") }}</div>
          <div class="font-medium">{{ device.user_identifier || "—" }}</div>
        </div>
        <div class="flex flex-col gap-0.5">
          <div class="text-muted-foreground">{{ $t("Статус") }}</div>
          <Tag :variant="statusVariant" size="sm" class="rounded-md">
            {{ DeviceStatusLabel[device.status] || device.status }}
          </Tag>
        </div>
        <div class="col-span-2">
          <div class="text-muted-foreground">Device ID</div>
          <div class="break-all">
            {{ device.device_id || "—" }}
          </div>
        </div>
        <div>
          <div class="text-muted-foreground">IP</div>
          <div class="">{{ device.ip_address || "—" }}</div>
        </div>
        <div>
          <div class="text-muted-foreground">
            {{ $t("Изменён статус") }}
          </div>
          <div>{{ formatDate(device.status_changed_at) }}</div>
        </div>
        <div>
          <div class="text-muted-foreground">{{ $t("Создан") }}</div>
          <div>{{ formatDate(device.created_at) }}</div>
        </div>
        <div>
          <div class="text-muted-foreground">{{ $t("Обновлён") }}</div>
          <div>{{ formatDate(device.changed_at) }}</div>
        </div>
      </div>

      <!-- Raw description toggle -->
      <!--      <div class="rounded-md border border-border p-3">-->
      <!--        <div class="cursor-pointer text-sm font-medium">-->
      <!--          {{ $t("Технические данные") }}-->
      <!--        </div>-->
      <!--        <div class="mt-2 max-h-72 overflow-auto rounded bg-muted p-3">-->
      <!--          {{ descriptionPretty }}-->
      <!--        </div>-->
      <!--      </div>-->

      <!-- Status change -->
      <div
        v-if="canEdit"
        class="flex flex-col gap-4 border-t border-border pt-4"
      >
        <!-- Статус -->
        <section class="card">
          <header class="card__header">
            <div class="flex items-center gap-2">
              <svg-icon name="huge-flag" class="h-5 w-5 text-foreground" />
              <span class="card__title">{{ $t("Статус") }}</span>
            </div>
          </header>
          <div class="card__body">
            <FormItem float>
              <FormLabel>{{ $t("Статус") }}</FormLabel>
              <FormControl>
                <Select v-model="form.status">
                  <SelectTrigger class="w-full">
                    <SelectValue :placeholder="$t('Выберите статус')" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem
                      v-for="(label, value) in selectableStatuses"
                      :key="value"
                      :value="value"
                    >
                      {{ label }}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
            </FormItem>
          </div>
        </section>

        <!-- Причина -->
        <section class="card">
          <header class="card__header">
            <div class="flex items-center gap-2">
              <svg-icon
                name="sli-clipboard"
                class="h-5 w-5 text-foreground"
                style="fill: none"
              />
              <span class="card__title">{{ $t("Причина") }}</span>
            </div>
          </header>
          <div class="card__body !gap-0">
            <FormItem float>
              <FormLabel>{{ $t("Рус") }}</FormLabel>
              <FormControl>
                <Textarea v-model="form.reason.ru" rows="2" />
              </FormControl>
            </FormItem>

            <FormItem float>
              <FormLabel>{{ $t("Каз") }}</FormLabel>
              <FormControl>
                <Textarea v-model="form.reason.kz" rows="2" />
              </FormControl>
            </FormItem>

            <FormItem float>
              <FormLabel>{{ $t("Анг") }}</FormLabel>
              <FormControl>
                <Textarea v-model="form.reason.en" rows="2" />
              </FormControl>
            </FormItem>
          </div>
        </section>

        <div v-if="formError" class="text-sm text-destructive">
          {{ formError }}
        </div>
      </div>
    </div>

    <template #footer>
      <div class="ml-auto flex gap-2">
        <Button variant="outline" @click="onOpenChange(false)">
          {{ $t("Закрыть") }}
        </Button>
        <Button
          v-if="canEdit"
          variant="default"
          :disabled="!canSubmit || saving"
          @click="submit"
        >
          <Loader2 v-if="saving" class="mr-2 h-4 w-4 animate-spin" />
          {{ $t("Сохранить") }}
        </Button>
      </div>
    </template>
  </FormSheet>
</template>

<script>
import { Loader2 } from "lucide-vue-next";
import { FormSheet } from "@/shared/ui/form-sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { Textarea } from "@/shared/ui/textarea";
import Button from "@/shared/ui/button/Button.vue";
import { Tag } from "@/shared/ui/tag";
import {
  DeviceStatus,
  DeviceStatusLabel,
  DeviceStatusVariant,
} from "@/shared/config/deviceStatuses";
import { FormItem, FormLabel, FormControl } from "@/shared/ui/form";
import dateFormat from "@/shared/lib/date-format";
import { updateUserDeviceStatus } from "@/entities/user-device";

function emptyReason() {
  return { ru: "", kz: "", en: "" };
}

export default {
  name: "DeviceDetailsDrawer",
  components: {
    FormSheet,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Textarea,
    Button,
    Tag,
    FormItem,
    FormLabel,
    FormControl,
    Loader2,
  },
  props: {
    open: { type: Boolean, default: false },
    device: { type: Object, default: null },
  },
  emits: ["update:open", "updated"],
  data() {
    return {
      form: {
        status: "",
        reason: emptyReason(),
      },
      saving: false,
      formError: "",
    };
  },
  computed: {
    canEdit() {
      return this.$store.getters.has_access("AUU");
    },
    statusVariant() {
      return DeviceStatusVariant[this.device?.status] || "outline";
    },
    DeviceStatusLabel() {
      return DeviceStatusLabel;
    },
    selectableStatuses() {
      // BLOCKED ставит сервер сам — через UI выставляем только ACTIVE/INACTIVE.
      return {
        [DeviceStatus.ACTIVE]: DeviceStatusLabel[DeviceStatus.ACTIVE],
        [DeviceStatus.INACTIVE]: DeviceStatusLabel[DeviceStatus.INACTIVE],
      };
    },
    // descriptionPretty() {
    //   const d = this.device?.description;
    //   if (d == null) return "—";
    //   try {
    //     return JSON.stringify(d, null, 2);
    //   } catch {
    //     return String(d);
    //   }
    // },
    canSubmit() {
      if (!this.form.status) return false;
      const r = this.form.reason;
      return Boolean(
        (r.ru && r.ru.trim()) || (r.kz && r.kz.trim()) || (r.en && r.en.trim())
      );
    },
  },
  watch: {
    open(value) {
      if (value) this.resetForm();
    },
    device: {
      immediate: true,
      handler() {
        // immediate, чтобы при первом монтировании drawer
        // (через v-if) форма сразу заполнилась данными device
        this.resetForm();
      },
    },
  },
  methods: {
    resetForm() {
      const d = this.device || {};
      // предзаполняем форму текущими значениями устройства,
      // чтобы пользователь видел, что меняет.
      this.form.status = d.status || "";

      // device.description — массив истории смен статуса:
      // [{ reason: {ru, kz, en}, from_status, to_status, changed_at, ... }, ...]
      // Берём reason из самой свежей записи (по changed_at).
      let reason = {};
      if (Array.isArray(d.description) && d.description.length) {
        const last = [...d.description].sort((a, b) => {
          const ta = new Date(a?.changed_at || 0).getTime();
          const tb = new Date(b?.changed_at || 0).getTime();
          return tb - ta;
        })[0];
        if (last?.reason && typeof last.reason === "object") {
          reason = last.reason;
        }
      } else if (d.reason && typeof d.reason === "object") {
        // на случай, если backend пришлёт плоский reason
        reason = d.reason;
      }

      this.form.reason = {
        ru: reason.ru || "",
        kz: reason.kz || "",
        en: reason.en || "",
      };
      this.formError = "";
    },
    onOpenChange(value) {
      this.$emit("update:open", value);
    },
    formatDate(value) {
      if (!value) return "—";
      return dateFormat.timestamp_to_local_tz(value, "DD MMM YYYY, HH:mm");
    },
    async submit() {
      if (!this.canSubmit || !this.device?.id) return;
      this.saving = true;
      this.formError = "";
      try {
        const payload = {
          status: this.form.status,
          reason: {
            ru: this.form.reason.ru?.trim() || "",
            kz: this.form.reason.kz?.trim() || "",
            en: this.form.reason.en?.trim() || "",
          },
        };
        const res = await updateUserDeviceStatus(this.device.id, payload);
        if (res?.success) {
          this.$emit("updated");
          this.onOpenChange(false);
        } else {
          this.formError =
            res?.payload?.detail || this.$t("Не удалось изменить статус");
        }
      } catch (err) {
        this.formError = err?.message || this.$t("Не удалось изменить статус");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.card {
  @apply rounded-md border border-border bg-card overflow-hidden;
}

.card__header {
  @apply flex items-center justify-between px-4 py-3 border-b border-border;
  background-color: hsl(var(--muted) / 0.4);
}

.card__title {
  @apply text-sm  text-foreground;
}

.card__body {
  @apply p-4 flex flex-col gap-3;
}

.info-badge {
  @apply inline-flex items-center justify-center h-5 w-5 rounded-full text-white text-xs font-semibold italic shrink-0 cursor-help;
  background-color: rgb(var(--color-blue-500));
  font-family: serif;
  line-height: 1;
}
</style>
